import { useEffect, useRef, type RefObject } from 'react';

/**
 * CORE-1724 (QA 22.10, iPhone and iPad, Safari and Chrome) — the time wheel's
 * column follows the finger by our own code, not by the browser's scroll.
 *
 * Both earlier fixes leaned on WebKit's native scroll with scroll-snap inside
 * the modal sheet. They held in Chrome's touch emulation, but on a real iOS
 * device (Chrome there is the same WebKit) the column still would not swipe.
 * Here the browser scrolls nothing: `touchmove` is cancelled, `scrollTop` is
 * set by hand, and on release the column coasts on and lands exactly on a row.
 */

/** How far the column coasts after release: release speed (px/ms) × this. */
const FLING_MS = 250;
/** Finger travel past which the touch is a swipe, not a tap on a number. */
const DRAG_SLOP_PX = 8;
/** The stretch of the gesture the release speed is measured over. */
const VELOCITY_WINDOW_MS = 100;

export interface TouchSample { t: number; y: number }

/** Scroll speed at release in px/ms; positive moves on to the later rows. */
export function releaseVelocity(samples: TouchSample[]): number {
  if (samples.length < 2) return 0;
  const last = samples[samples.length - 1];
  const first = samples.find((s) => last.t - s.t <= VELOCITY_WINDOW_MS) ?? samples[0];
  const dt = last.t - first.t;
  return dt > 0 ? (first.y - last.y) / dt : 0;
}

/** The row offset the column comes to rest on: coasting included, inside the list. */
export function settleTop(top: number, velocity: number, pitch: number, count: number): number {
  const index = Math.round((top + velocity * FLING_MS) / pitch);
  return Math.min(count - 1, Math.max(0, index)) * pitch;
}

const easeOutCubic = (p: number) => 1 - (1 - p) ** 3;

export interface TouchWheelOptions {
  /** Distance between the centres of two rows. */
  pitch: number;
  count: number;
  /** Called with the final offset once the column has come to rest. */
  onSettle: (top: number) => void;
}

/** Drives `el`'s scroll from touches; returns the detach function. */
export function attachTouchWheel(el: HTMLElement, options: TouchWheelOptions): () => void {
  let startY = 0;
  let startTop = 0;
  let samples: TouchSample[] = [];
  let dragged = false;
  let frame = 0;

  const stop = () => {
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
  };
  const maxTop = () => Math.max(0, el.scrollHeight - el.clientHeight);

  const coastTo = (target: number) => {
    const from = el.scrollTop;
    const duration = Math.min(600, Math.max(180, Math.abs(target - from) * 1.5));
    const t0 = performance.now();
    const step = (now: number) => {
      const p = Math.min(1, (now - t0) / duration);
      el.scrollTop = from + (target - from) * easeOutCubic(p);
      if (p < 1) {
        frame = requestAnimationFrame(step);
        return;
      }
      frame = 0;
      el.style.scrollSnapType = '';
      options.onSettle(target);
    };
    frame = requestAnimationFrame(step);
  };

  const onStart = (e: TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    stop();
    // Browser snapping would pull every one of our steps back to the nearest row.
    el.style.scrollSnapType = 'none';
    startY = touch.clientY;
    startTop = el.scrollTop;
    samples = [{ t: e.timeStamp, y: touch.clientY }];
    dragged = false;
  };
  const onMove = (e: TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    if (e.cancelable) e.preventDefault();
    const dy = touch.clientY - startY;
    if (Math.abs(dy) > DRAG_SLOP_PX) dragged = true;
    el.scrollTop = Math.min(maxTop(), Math.max(0, startTop - dy));
    samples.push({ t: e.timeStamp, y: touch.clientY });
    if (samples.length > 20) samples.shift();
  };
  const onEnd = () => {
    if (!dragged) {
      el.style.scrollSnapType = '';
      return;
    }
    coastTo(settleTop(el.scrollTop, releaseVelocity(samples), options.pitch, options.count));
  };
  // A swipe released over a row is not a tap on it.
  const onClick = (e: MouseEvent) => {
    if (!dragged) return;
    dragged = false;
    e.preventDefault();
    e.stopPropagation();
  };

  el.addEventListener('touchstart', onStart, { passive: true });
  el.addEventListener('touchmove', onMove, { passive: false });
  el.addEventListener('touchend', onEnd);
  el.addEventListener('touchcancel', onEnd);
  el.addEventListener('click', onClick, true);
  return () => {
    stop();
    el.style.scrollSnapType = '';
    el.removeEventListener('touchstart', onStart);
    el.removeEventListener('touchmove', onMove);
    el.removeEventListener('touchend', onEnd);
    el.removeEventListener('touchcancel', onEnd);
    el.removeEventListener('click', onClick, true);
  };
}

/**
 * The wheel on a ScrollView column while `enabled`. On react-native-web the
 * ScrollView ref is the component; its DOM node comes from `getScrollableNode`.
 */
export function useTouchWheel(ref: RefObject<unknown>, enabled: boolean, options: TouchWheelOptions) {
  const latest = useRef(options);
  latest.current = options;
  const { pitch, count } = options;
  useEffect(() => {
    if (!enabled) return;
    const host = ref.current as { getScrollableNode?: () => HTMLElement } | null;
    const node = host?.getScrollableNode?.() ?? (host as unknown as HTMLElement | null);
    if (!node || typeof node.addEventListener !== 'function') return;
    return attachTouchWheel(node, { pitch, count, onSettle: (top) => latest.current.onSettle(top) });
  }, [ref, enabled, pitch, count]);
}
